import { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Rating, Stack, Chip, TextField, Typography, Box,
} from '@mui/material';

const PRESET_REASONS = [
  'Wrong role',
  'Wrong company',
  'Contact looks fake',
  'Already contacted',
  'Low quality',
  'Other',
];

export interface ContactDownvoteDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: { rating: number; reasonChips: string[]; reasonText?: string }) => Promise<void> | void;
  onRemove?: () => Promise<void> | void;       // shown only when editing existing downvote
  initial?: { rating?: number | null; reasonChips?: string[]; reasonText?: string | null } | null;
  contactName?: string;
  contactPosition?: string;
}

export default function ContactDownvoteDialog({
  open, onClose, onSubmit, onRemove, initial, contactName, contactPosition,
}: ContactDownvoteDialogProps) {
  const [rating, setRating] = useState<number | null>(initial?.rating ?? null);
  const [reasonChips, setReasonChips] = useState<string[]>(initial?.reasonChips ?? []);
  const [reasonText, setReasonText] = useState<string>(initial?.reasonText ?? '');
  const [submitting, setSubmitting] = useState(false);

  // Reset state when the dialog opens for a different contact / vote
  useEffect(() => {
    if (open) {
      setRating(initial?.rating ?? null);
      setReasonChips(initial?.reasonChips ?? []);
      setReasonText(initial?.reasonText ?? '');
    }
  }, [open, initial?.rating, initial?.reasonChips, initial?.reasonText]);

  const toggleChip = (chip: string) => {
    setReasonChips(prev => prev.includes(chip) ? prev.filter(c => c !== chip) : [...prev, chip]);
  };

  const handleSubmit = async () => {
    if (rating == null) return;
    setSubmitting(true);
    try {
      await onSubmit({
        rating,
        reasonChips,
        reasonText: reasonText.trim() || undefined,
      });
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemove = async () => {
    if (!onRemove) return;
    setSubmitting(true);
    try {
      await onRemove();
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>
        Why is this contact a poor fit?
        {(contactName || contactPosition) && (
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontWeight: 400, mt: 0.5 }}>
            {[contactName, contactPosition].filter(Boolean).join(' · ')}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Quality rating *</Typography>
            <Box sx={{ mt: 0.5 }}>
              <Rating
                value={rating}
                onChange={(_, v) => setRating(v)}
                size="large"
              />
            </Box>
          </Box>

          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.75 }}>
              Reasons (optional)
            </Typography>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              {PRESET_REASONS.map(chip => (
                <Chip
                  key={chip}
                  label={chip}
                  size="small"
                  clickable
                  color={reasonChips.includes(chip) ? 'primary' : 'default'}
                  variant={reasonChips.includes(chip) ? 'filled' : 'outlined'}
                  onClick={() => toggleChip(chip)}
                />
              ))}
            </Stack>
          </Box>

          <TextField
            multiline
            minRows={2}
            maxRows={5}
            fullWidth
            placeholder="Tell us more (optional)"
            value={reasonText}
            onChange={e => setReasonText(e.target.value)}
            size="small"
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
        <Box>
          {onRemove && (
            <Button color="error" size="small" onClick={handleRemove} disabled={submitting}>
              Remove vote
            </Button>
          )}
        </Box>
        <Box>
          <Button onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={rating == null || submitting}
            sx={{ ml: 1 }}
          >
            {submitting ? 'Saving…' : 'Submit'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
